import React, { useEffect, useState } from 'react';
import { BrowserRouter, useLocation, useNavigate } from 'react-router-dom';
import AppRoutes from './routes/AppRoutes';
import { CssBaseline, ThemeProvider } from '@mui/material';
import getTheme from './theme/theme';
import { PinnacleThemes } from './theme/types';
import { PinnacleSnackbarProvider } from './components/PinnacleSnackbarProvider';
import { NotificationTypes } from './components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types';
import { pushNotification } from './components/PinnacleSnackbarProvider/PinnacleSnackbarProvider';
import { TokenErrorModal } from './components/common/TokenErrorModal/TokenErrorModal';

const AppContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showTokenExpiredModal, setShowTokenExpiredModal] = useState(false);
  const [showDeactivatedModal, setShowDeactivatedModal] = useState(false);

  useEffect(() => {
    if (location.pathname === "/login") return;

    const onTokenExpired = () => {
      setShowTokenExpiredModal(true);
      const timer = setTimeout(() => {
        setShowTokenExpiredModal(false);
        navigate("/login");
      }, 3000);
      return () => clearTimeout(timer);
    };

    const onDeactivated = () => {
      setShowDeactivatedModal(true);
      const timer = setTimeout(() => {
        setShowDeactivatedModal(false);
        navigate("/login");
      }, 3000);
      return () => clearTimeout(timer);
    };

    const onUnauthorized = () => {
      setShowTokenExpiredModal(true);
      const timer = setTimeout(() => {
        setShowTokenExpiredModal(false);
        navigate("/login");
      }, 3000);
      return () => clearTimeout(timer);
    };

    const onServerError = () => {
      pushNotification({
        details: 'Server error. Please try again later.',
        type: NotificationTypes.Error,
      });
    };

    window.addEventListener('api:tokenExpired', onTokenExpired);
    window.addEventListener('api:deactivatedAccount', onDeactivated);
    window.addEventListener('api:unauthorized', onUnauthorized);
    window.addEventListener('api:serverError', onServerError);

    return () => {
      window.removeEventListener('api:tokenExpired', onTokenExpired);
      window.removeEventListener('api:deactivatedAccount', onDeactivated);
      window.removeEventListener('api:unauthorized', onUnauthorized);
      window.removeEventListener('api:serverError', onServerError);
    };
  }, [navigate]);

  return (
    <>
      <AppRoutes />
      <TokenErrorModal type="expired" open={showTokenExpiredModal} />
      <TokenErrorModal type="deactivated" open={showDeactivatedModal} />
    </>
  );
};

const App: React.FC = () => {
  const theme = getTheme(PinnacleThemes.BASE_THEME);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <PinnacleSnackbarProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </PinnacleSnackbarProvider>
    </ThemeProvider>
  );
};

export default App;
